"use client";

import axios from "axios";
import imageCompression from "browser-image-compression";

// Updated base URL to use the development server
const baseURL = process.env.REACT_APP_ADMIN_URL || 'https://dev.server.kiwikrave.com';
export const mediaUploadURL = "https://kiwikiosk.nyc3.digitaloceanspaces.com/media/kiwikiosk_items/";
export const dashboardURL = process.env.REACT_APP_DASHBOARD_URL || 'http://localhost:3000';

export const getCompressedFile = async (file) => {
    const compressionOptions = {
        maxWidthOrHeight: file.width,
        useWebWorker: true
    };

    try {
        const compressedFile = await imageCompression(file, compressionOptions);

        // Get the original file name without the extension
        const originalFileName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;

        // Generate a unique name by appending a timestamp
        const uniqueFileName = `${originalFileName}-${Date.now()}.jpg`; // Change extension if necessary

        // Create a new File object with the unique name
        return new File(
            [compressedFile],
            uniqueFileName,
            {type: compressedFile.type}
        );

    } catch (error) {
        console.error("Compression error:", error);
        throw error;
    }
};

// const convertToWebP = async (file) => {
//     const originalFileName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;

//     return new Promise((resolve, reject) => {
//         const reader = new FileReader();
//         reader.readAsDataURL(file);

//         reader.onloadend = () => {
//             const img = new Image();
//             img.src = reader.result;
//             img.onload = () => {
//                 const canvas = document.createElement('canvas');
//                 canvas.width = img.width;
//                 canvas.height = img.height;
//                 const ctx = canvas.getContext('2d');
//                 ctx.drawImage(img, 0, 0);

//                 canvas.toBlob(
//                     (blob) => {
//                         if (blob) {
//                             resolve(new File([blob], `${originalFileName}.webp`, { type: 'image/webp' }));
//                         } else {
//                             reject(new Error("Failed to create WebP blob."));
//                         }
//                     },
//                     'image/webp',
//                     0.95 // Use a higher quality setting
//                 );
//             };

//             img.onerror = (err) => reject(err);
//         };

//         reader.onerror = (err) => reject(err);
//     });
// };

const API = axios.create({
    baseURL: baseURL,
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // Add cache-busting headers
        "Cache-Control": "no-cache, no-store, must-revalidate",
        "Pragma": "no-cache",
        "Expires": "0"
    },
});

// Update the request interceptor
API.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        // Only add token if it exists and is not null
        if (token && token !== 'null' && token !== 'undefined') {
            config.headers.Authorization = `Bearer ${token}`;
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

API.interceptors.response.use(
    response => {
      return response;
    },
    error => {
        if (error.response && error.response.status === 401) {
            const isLoginRequest = error.config?.url?.includes('/portal/login');
            const role = localStorage.getItem('role');
            const isManagerOrStaff = role === 'merchant_manager' || role === 'merchant_staff';
            if (!isLoginRequest && !isManagerOrStaff) {
                localStorage.removeItem('access_token');
                localStorage.removeItem('user');
                localStorage.removeItem('role');
                window.location.href = "/logout";
            }
        }
        return Promise.reject(error);
    }
);

// Helper function to clean picture paths from full URLs to relative paths
export const cleanPicturePath = (path) => {
    
    if (!path || path.trim() === "") {
        return "default.jpg";
    }
    
    // List of possible base URLs to clean - in order of specificity
    const baseURLs = [
        "https://kiwikiosk.nyc3.cdn.digitaloceanspaces.com/media/kiwikiosk_items/",
        "https://kiwikiosk.nyc3.cdn.digitaloceanspaces.com/media/",
        "https://kiwikiosk.nyc3.cdn.digitaloceanspaces.com/kiwikiosk_items/",
        "https://kiwikiosk.nyc3.cdn.digitaloceanspaces.com/",
        "https://kiwikiosk.nyc3.digitaloceanspaces.com/media/kiwikiosk_items/",
        "https://kiwikiosk.nyc3.digitaloceanspaces.com/media/",
        "https://kiwikiosk.nyc3.digitaloceanspaces.com/kiwikiosk_items/",
        "https://kiwikiosk.nyc3.digitaloceanspaces.com/"
    ];
    
    // Check if path starts with any of the base URLs
    for (const baseURL of baseURLs) {
        if (path.startsWith(baseURL)) {
            const result = path.substring(baseURL.length);
            return result;
        }
    }
    
    // If no base URL matches, try to extract the path after the domain
    const urlParts = path.split(/https?:\/\/[^/]+\//);
    const result = urlParts[urlParts.length - 1];
    return result;
};

// Helper function to get the full media URL
export const getMediaURL = (path) => {
    
    if (!path || path.trim() === "" || path === "{}" || path === "%7B%7D") {
        return null;
    }
    
    // If path is already a complete URL, we need to validate and potentially fix it
    if (path.startsWith('http://') || path.startsWith('https://')) {
        // Check for corrupted double URLs
        if (path.includes('https://nyc3.digitaloceanspaces.com/kiwikiosk/media/https://')) {
            // Extract the inner URL
            const match = path.match(/https:\/\/nyc3\.digitaloceanspaces\.com\/kiwikiosk\/media\/(https:\/\/.+)/);
            if (match && match[1]) {
                path = match[1];
            }
        }
        
        // Fix storage domain URLs (nyc3.digitaloceanspaces.com -> kiwikiosk.nyc3.cdn.digitaloceanspaces.com)
        if (path.includes('nyc3.digitaloceanspaces.com/kiwikiosk/')) {
            path = path.replace('https://nyc3.digitaloceanspaces.com/kiwikiosk/', 'https://kiwikiosk.nyc3.cdn.digitaloceanspaces.com/');
        }
        
        // Fix URLs missing /media/ prefix
        if (path.includes('kiwikiosk.nyc3.cdn.digitaloceanspaces.com/') || 
            path.includes('kiwikiosk.nyc3.digitaloceanspaces.com/')) {
            
            // Check if it's missing /media/ prefix
            if (path.match(/digitaloceanspaces\.com\/(kiwikiosk_items|download)/)) {
                path = path.replace(
                    /digitaloceanspaces\.com\/(kiwikiosk_items|download)/,
                    'digitaloceanspaces.com/media/$1'
                );
            }
        }
        
        return path;
    }
    
    // Clean the path - remove any leading slashes
    let cleanPath = path;
    if (cleanPath.startsWith('/')) {
        cleanPath = cleanPath.substring(1);
    }
    
    
    // Handle different path structures from the server
    // Images can be stored in multiple locations on the CDN:
    // 1. Root level (legacy): download.jpg
    // 2. kiwikiosk_items/ (legacy): kiwikiosk_items/image.jpg  
    // 3. media/kiwikiosk_items/ (current): media/kiwikiosk_items/image.jpg
    
    let finalPath;
    
    if (cleanPath.startsWith('media/kiwikiosk_items/')) {
        // Already has the full path - keep as is
        finalPath = cleanPath;
    } else if (cleanPath.startsWith('media/')) {
        // Has media/ prefix - keep as is
        finalPath = cleanPath;
    } else if (cleanPath.startsWith('kiwikiosk_items/')) {
        // Has kiwikiosk_items/ prefix but no media/
        // Keep as is - this is a valid legacy path
        finalPath = cleanPath;
    } else {
        // No prefix at all - just the filename
        // Check if it looks like a root-level file
        if (cleanPath.includes('download') && !cleanPath.includes('/')) {
            // Likely a root-level file - keep as is
            finalPath = cleanPath;
        } else {
            // Default: assume it should be in kiwikiosk_items/
            finalPath = `kiwikiosk_items/${cleanPath}`;
        }
    }
    
    const result = `https://kiwikiosk.nyc3.cdn.digitaloceanspaces.com/${finalPath}`;
    return result;
};

export default API;
